import { useEffect, useState } from "react";
import {
  arrayUnion,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  useNavigate,
  useParams,
} from "react-router-dom";
import { auth, db } from "../firebase";

function AdminLeadDetails() {
  const navigate = useNavigate();
  const { leadId } = useParams();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [message, setMessage] =
    useState("");

  const [formData, setFormData] =
    useState({
      customerName: "",
      phone: "",
      alternatePhone: "",
      email: "",
      source: "",
      requirement: "",
      priority: "Normal",
    });

  useEffect(() => {
    const loadLead = async () => {
      try {
        setLoading(true);

        const leadSnapshot =
          await getDoc(
            doc(
              db,
              "leads",
              leadId
            )
          );

        if (
          leadSnapshot.exists()
        ) {
          setLead({
            id:
              leadSnapshot.id,

            ...leadSnapshot.data(),
          });
        }
      } catch (error) {
        console.error(
          "Lead detail load error:",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    loadLead();
  }, [leadId]);

  const toDate = (
    value
  ) => {
    if (!value) {
      return null;
    }

    try {
      if (
        typeof value.toDate ===
        "function"
      ) {
        return value.toDate();
      }

      const date =
        new Date(value);

      if (
        Number.isNaN(
          date.getTime()
        )
      ) {
        return null;
      }

      return date;
    } catch (error) {
      return null;
    }
  };

  const formatDate = (
    value
  ) => {
    const date =
      toDate(value);

    if (!date) {
      return "-";
    }

    return date.toLocaleString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  };

  const startEditing = () => {
    if (!lead) {
      return;
    }

    setFormData({
      customerName:
        lead.customerName ||
        lead.name ||
        "",

      phone:
        lead.phone || "",

      alternatePhone:
        lead.alternatePhone ||
        lead.alternateNumber ||
        "",

      email:
        lead.email || "",

      source:
        lead.source || "",

      requirement:
        lead.requirement || "",

      priority:
        lead.priority ||
        "Normal",
    });

    setEditing(true);
    setMessage("");
  };

  const handleInputChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target;

    setFormData(
      (current) => ({
        ...current,
        [name]: value,
      })
    );
  };

  const handleSave = async () => {
    if (!lead) {
      return;
    }

    const newValues = {
      customerName:
        formData.customerName.trim(),

      phone:
        formData.phone.trim(),

      alternatePhone:
        formData.alternatePhone.trim(),

      email:
        formData.email.trim(),

      source:
        formData.source.trim(),

      requirement:
        formData.requirement.trim(),

      priority:
        formData.priority ||
        "Normal",
    };

    if (
      !newValues.customerName
    ) {
      setMessage(
        "Customer name is required."
      );

      return;
    }

    if (
      !newValues.phone
    ) {
      setMessage(
        "Primary phone number is required."
      );

      return;
    }

    const fields = [
      {
        key:
          "customerName",

        label:
          "Customer Name",

        before:
          lead.customerName ||
          lead.name ||
          "",
      },

      {
        key:
          "phone",

        label:
          "Primary Number",

        before:
          lead.phone || "",
      },

      {
        key:
          "alternatePhone",

        label:
          "Alternate Number",

        before:
          lead.alternatePhone ||
          lead.alternateNumber ||
          "",
      },

      {
        key:
          "email",

        label:
          "Email",

        before:
          lead.email || "",
      },

      {
        key:
          "source",

        label:
          "Lead Source",

        before:
          lead.source || "",
      },

      {
        key:
          "requirement",

        label:
          "Customer Requirement",

        before:
          lead.requirement || "",
      },

      {
        key:
          "priority",

        label:
          "Priority",

        before:
          lead.priority ||
          "Normal",
      },
    ];

    const changes =
      fields
        .filter(
          (field) =>
            String(
              field.before ??
              ""
            ) !==
            String(
              newValues[
                field.key
              ] ??
              ""
            )
        )

        .map(
          (field) => ({
            field:
              field.key,

            label:
              field.label,

            before:
              String(
                field.before ??
                ""
              ),

            after:
              String(
                newValues[
                  field.key
                ] ??
                ""
              ),
          })
        );

    if (
      changes.length ===
      0
    ) {
      setMessage(
        "No lead detail changes to save."
      );

      setEditing(false);

      return;
    }

    try {
      setSaving(true);
      setMessage("");

      const changedAt =
        new Date()
          .toISOString();

      const editEntry = {
        changedById:
          auth.currentUser
            ?.uid ||
          "",

        changedByName:
          "Admin",

        changedByEmail:
          auth.currentUser
            ?.email ||
          "",

        changedByRole:
          "Admin",

        changedAt,

        changes,
      };

      const updateData = {
        customerName:
          newValues.customerName,

        name:
          newValues.customerName,

        phone:
          newValues.phone,

        alternatePhone:
          newValues.alternatePhone,

        email:
          newValues.email,

        source:
          newValues.source,

        requirement:
          newValues.requirement,

        priority:
          newValues.priority,

        leadEditHistory:
          arrayUnion(
            editEntry
          ),

        updatedAt:
          serverTimestamp(),
      };

      await updateDoc(
        doc(
          db,
          "leads",
          lead.id
        ),
        updateData
      );

      const oldHistory =
        Array.isArray(
          lead.leadEditHistory
        )
          ? lead.leadEditHistory
          : [];

      setLead({
        ...lead,

        ...updateData,

        leadEditHistory: [
          ...oldHistory,
          editEntry,
        ],
      });

      setEditing(false);

      setMessage(
        "Lead details updated successfully."
      );
    } catch (error) {
      console.error(
        "Admin lead edit error:",
        error
      );

      setMessage(
        `Unable to update lead: ${
          error.message ||
          "Firebase permission error"
        }`
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="employee-empty-state">
        Loading customer details...
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="employee-empty-state">
        Customer record not found.
      </div>
    );
  }

  const remarksHistory =
    Array.isArray(
      lead.remarksHistory
    )
      ? [
          ...lead.remarksHistory,
        ].reverse()
      : [];

  const reassignmentHistory =
    Array.isArray(
      lead.reassignmentHistory
    )
      ? [
          ...lead.reassignmentHistory,
        ].reverse()
      : [];

  const leadEditHistory =
    Array.isArray(
      lead.leadEditHistory
    )
      ? [
          ...lead.leadEditHistory,
        ].reverse()
      : [];

  return (
    <div className="admin-dashboard-page">

      <header className="dashboard-header">

        <div>
          <h1>
            Trading Wealth CRM
          </h1>

          <p>
            Customer Details
          </p>
        </div>

        <button
          type="button"
          className="logout-button"
          onClick={() =>
            navigate(-1)
          }
        >
          Back
        </button>

      </header>

      <main className="dashboard-main">

        <section className="dashboard-welcome">

          <p className="dashboard-label">
            CUSTOMER RECORD
          </p>

          <h2>
            {lead.customerName ||
              lead.name ||
              "Customer"}
          </h2>

          <p>
            Complete CRM history and assignment details.
          </p>

        </section>

        {message && (
          <div className="attendance-message">
            {message}
          </div>
        )}

        <section className="employee-form-card">

          <div className="section-heading">

            <div>

              <p className="dashboard-label">
                CUSTOMER DETAILS
              </p>

              <h2>
                Lead Information
              </h2>

            </div>

            {!editing && (
              <button
                type="button"
                className="secondary-button"
                onClick={
                  startEditing
                }
              >
                Edit Lead Details
              </button>
            )}

          </div>

          {editing ? (
            <>

              <div className="employee-form-grid">

                <div className="form-group">

                  <label>
                    Customer Name *
                  </label>

                  <input
                    type="text"
                    name="customerName"
                    value={
                      formData.customerName
                    }
                    onChange={
                      handleInputChange
                    }
                  />

                </div>

                <div className="form-group">

                  <label>
                    Primary Number *
                  </label>

                  <input
                    type="tel"
                    name="phone"
                    value={
                      formData.phone
                    }
                    onChange={
                      handleInputChange
                    }
                  />

                </div>

                <div className="form-group">

                  <label>
                    Alternate Number
                  </label>

                  <input
                    type="tel"
                    name="alternatePhone"
                    value={
                      formData.alternatePhone
                    }
                    onChange={
                      handleInputChange
                    }
                    placeholder="Add alternate number"
                  />

                </div>

                <div className="form-group">

                  <label>
                    Email
                  </label>

                  <input
                    type="email"
                    name="email"
                    value={
                      formData.email
                    }
                    onChange={
                      handleInputChange
                    }
                  />

                </div>

                <div className="form-group">

                  <label>
                    Lead Source
                  </label>

                  <input
                    type="text"
                    name="source"
                    value={
                      formData.source
                    }
                    onChange={
                      handleInputChange
                    }
                  />

                </div>

                <div className="form-group">

                  <label>
                    Priority
                  </label>

                  <select
                    name="priority"
                    value={
                      formData.priority
                    }
                    onChange={
                      handleInputChange
                    }
                  >

                    <option value="Low">
                      Low
                    </option>

                    <option value="Normal">
                      Normal
                    </option>

                    <option value="High">
                      High
                    </option>

                    <option value="Urgent">
                      Urgent
                    </option>

                  </select>

                </div>

                <div
                  className="form-group"
                  style={{
                    gridColumn:
                      "1 / -1",
                  }}
                >

                  <label>
                    Customer Requirement
                  </label>

                  <textarea
                    className="crm-textarea"
                    name="requirement"
                    value={
                      formData.requirement
                    }
                    onChange={
                      handleInputChange
                    }
                  />

                </div>

              </div>

              <div
                className="employee-form-actions"
                style={{
                  marginTop:
                    "15px",
                }}
              >

                <button
                  type="button"
                  className="admin-login-button employee-save-button"
                  onClick={
                    handleSave
                  }
                  disabled={
                    saving
                  }
                >
                  {saving
                    ? "Saving..."
                    : "Save Lead Details"}
                </button>

                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => {
                    setEditing(
                      false
                    );

                    setMessage(
                      ""
                    );
                  }}
                  disabled={
                    saving
                  }
                >
                  Cancel
                </button>

              </div>

            </>
          ) : (

            <div className="employee-form-grid">

              <div className="form-group">

                <label>
                  Customer Name
                </label>

                <div>
                  {lead.customerName ||
                    lead.name ||
                    "-"}
                </div>

              </div>

              <div className="form-group">

                <label>
                  Primary Phone
                </label>

                <div>
                  {lead.phone ||
                    "-"}
                </div>

              </div>

              <div className="form-group">

                <label>
                  Alternate Phone
                </label>

                <div>
                  {lead.alternatePhone ||
                    lead.alternateNumber ||
                    "-"}
                </div>

              </div>

              <div className="form-group">

                <label>
                  Email
                </label>

                <div>
                  {lead.email ||
                    "-"}
                </div>

              </div>

              <div className="form-group">

                <label>
                  Current Status
                </label>

                <div>
                  {lead.status ||
                    "Pending"}
                </div>

              </div>

              <div className="form-group">

                <label>
                  Assigned Employee
                </label>

                <div>
                  {lead.assignedEmployeeName ||
                    "-"}
                </div>

              </div>

              <div className="form-group">

                <label>
                  Employee Email
                </label>

                <div>
                  {lead.assignedEmployeeEmail ||
                    "-"}
                </div>

              </div>

              <div className="form-group">

                <label>
                  Priority
                </label>

                <div>
                  {lead.priority ||
                    "Normal"}
                </div>

              </div>

              <div className="form-group">

                <label>
                  Lead Source
                </label>

                <div>
                  {lead.source ||
                    "-"}
                </div>

              </div>

              <div className="form-group">

                <label>
                  Customer Requirement
                </label>

                <div>
                  {lead.requirement ||
                    "-"}
                </div>

              </div>

              <div className="form-group">

                <label>
                  Callback Date
                </label>

                <div>
                  {lead.callbackDate ||
                    "-"}
                </div>

              </div>

              <div className="form-group">

                <label>
                  Callback Time
                </label>

                <div>
                  {lead.callbackTime ||
                    "-"}
                </div>

              </div>

              <div className="form-group">

                <label>
                  Last Contact
                </label>

                <div>
                  {formatDate(
                    lead.lastContactedAt
                  )}
                </div>

              </div>

              <div className="form-group">

                <label>
                  Created
                </label>

                <div>
                  {formatDate(
                    lead.createdAt
                  )}
                </div>

              </div>

            </div>

          )}

        </section>

        <section
          className="remark-history-card"
          style={{
            marginTop:
              "20px",
          }}
        >

          <h3>
            Lead Detail Edit History
          </h3>

          {leadEditHistory.length ===
          0 ? (

            <div className="employee-empty-state">
              No lead detail edits recorded.
            </div>

          ) : (

            <div className="remark-timeline">

              {leadEditHistory.map(
                (
                  editItem,
                  index
                ) => (

                  <div
                    key={
                      `${
                        editItem.changedAt ||
                        "edit"
                      }-${index}`
                    }
                    className="remark-history-item"
                  >

                    <div className="remark-history-top">

                      <div>

                        <strong>
                          {editItem.changedByName ||
                            "User"}
                        </strong>

                        <div
                          style={{
                            fontSize:
                              "12px",

                            color:
                              "#64748b",
                          }}
                        >
                          {editItem.changedByRole ||
                            ""}

                          {editItem.changedByEmail
                            ? ` • ${editItem.changedByEmail}`
                            : ""}
                        </div>

                      </div>

                      <span>
                        {formatDate(
                          editItem.changedAt
                        )}
                      </span>

                    </div>

                    {Array.isArray(
                      editItem.changes
                    ) &&
                      editItem.changes.map(
                        (
                          change,
                          changeIndex
                        ) => (

                          <div
                            key={
                              `${
                                change.field ||
                                "field"
                              }-${changeIndex}`
                            }
                            style={{
                              marginTop:
                                "10px",

                              padding:
                                "10px",

                              border:
                                "1px solid #e2e8f0",

                              borderRadius:
                                "8px",
                            }}
                          >

                            <strong>
                              {change.label ||
                                change.field}
                            </strong>

                            <p
                              style={{
                                marginTop:
                                  "5px",
                              }}
                            >
                              Before:{" "}
                              {change.before ||
                                "-"}
                            </p>

                            <p>
                              After:{" "}
                              {change.after ||
                                "-"}
                            </p>

                          </div>

                        )
                      )}

                  </div>

                )
              )}

            </div>

          )}

        </section>

        <section
          className="remark-history-card"
          style={{
            marginTop:
              "20px",
          }}
        >

          <h3>
            Full Remark History
          </h3>

          {remarksHistory.length ===
          0 ? (

            <div className="employee-empty-state">
              No previous remarks.
            </div>

          ) : (

            <div className="remark-timeline">

              {remarksHistory.map(
                (
                  historyItem,
                  index
                ) => (

                  <div
                    key={
                      `${
                        historyItem.createdAt ||
                        "history"
                      }-${index}`
                    }
                    className="remark-history-item"
                  >

                    <div className="remark-history-top">

                      <div>

                        <strong>
                          {historyItem.employeeName ||
                            "Employee"}
                        </strong>

                        <div
                          style={{
                            fontSize:
                              "12px",

                            color:
                              "#64748b",
                          }}
                        >
                          {historyItem.employeeEmail ||
                            ""}
                        </div>

                      </div>

                      <span>
                        {formatDate(
                          historyItem.createdAt
                        )}
                      </span>

                    </div>

                    <p>

                      <strong>
                        {historyItem.connectionType ||
                          historyItem.disposition ||
                          historyItem.status ||
                          "Call Update"}
                      </strong>

                    </p>

                    <p>
                      {historyItem.discussion ||
                        historyItem.remark ||
                        historyItem.subRemark ||
                        historyItem.disposition ||
                        "-"}
                    </p>

                    {historyItem.callbackDate && (

                      <small>
                        Callback:{" "}
                        {
                          historyItem.callbackDate
                        }{" "}
                        {
                          historyItem.callbackTime
                        }
                      </small>

                    )}

                    {historyItem.language && (

                      <small
                        style={{
                          display:
                            "block",

                          marginTop:
                            "5px",
                        }}
                      >
                        Language:{" "}
                        {
                          historyItem.language
                        }
                      </small>

                    )}

                    {historyItem.transferredToEmployeeName && (

                      <small
                        style={{
                          display:
                            "block",

                          marginTop:
                            "5px",
                        }}
                      >
                        Transferred To:{" "}
                        {
                          historyItem.transferredToEmployeeName
                        }
                      </small>

                    )}

                  </div>

                )
              )}

            </div>

          )}

        </section>

        <section
          className="remark-history-card"
          style={{
            marginTop:
              "20px",
          }}
        >

          <h3>
            Reassignment / Churn History
          </h3>

          {reassignmentHistory.length ===
          0 ? (

            <div className="employee-empty-state">
              No reassignment history.
            </div>

          ) : (

            <div className="remark-timeline">

              {reassignmentHistory.map(
                (
                  item,
                  index
                ) => (

                  <div
                    key={
                      index
                    }
                    className="remark-history-item"
                  >

                    <strong>
                      {item.fromEmployeeName ||
                        "-"}{" "}
                      →{" "}
                      {item.toEmployeeName ||
                        "-"}
                    </strong>

                    <p>
                      {formatDate(
                        item.changedAt
                      )}
                    </p>

                    <small>
                      Changed By:{" "}
                      {item.changedBy ||
                        "Admin"}
                    </small>

                  </div>

                )
              )}

            </div>

          )}

        </section>

      </main>

    </div>
  );
}

export default AdminLeadDetails;